import type { EditorResponse, SectionGroup, SectionMetadata } from './types';

/**
 * Thin wrapper around the lc-store-engine HTTP surface.
 *
 * In dev, requests go to `/engine/*` which Vite rewrites onto the engine.
 * In prod, set `VITE_ENGINE_URL` to point at the engine directly.
 */

const ENGINE_BASE = (import.meta as any).env?.VITE_ENGINE_URL || '/engine';

export interface EditorSession {
  tenantId: string;
  themeId: string;
  token: string;
  storeHost?: string;
}

function headers(session: EditorSession, extra?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.token}`,
    ...(session.storeHost ? { 'X-Store-Host': session.storeHost } : {}),
    ...(extra || {}),
  };
}

async function request<T>(url: string, init: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const text = await res.text();
  let data: any;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = typeof data === 'object' && data?.error ? data.error : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Editor: full page render
// ---------------------------------------------------------------------------

export async function fetchEditorPage(
  session: EditorSession,
  page: string,
  opts?: {
    nocache?: boolean;
    productSlug?: string;
    collectionId?: string;
    drafts?: { files?: Record<string, string>; globalSettings?: Record<string, any> };
  }
): Promise<EditorResponse> {
  const params = new URLSearchParams();
  if (opts?.nocache) params.set('nocache', '1');
  // The engine accepts `productSlug` (preferred) or `productId` on the
  // /render-for-editor route — NOT `product`. Sending the wrong param
  // silently dropped the picker's selection, so the preview always
  // rendered with whatever mock/default the engine had.
  if (opts?.productSlug) params.set('productSlug', opts.productSlug);
  if (opts?.collectionId) params.set('collectionId', opts.collectionId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${ENGINE_BASE}/render-for-editor/${session.tenantId}/${session.themeId}/${page}${qs}`;
  if (opts?.drafts) {
    return request<EditorResponse>(url, {
      method: 'POST',
      headers: headers(session),
      body: JSON.stringify({ drafts: opts.drafts }),
    });
  }
  return request<EditorResponse>(url, { method: 'GET', headers: headers(session) });
}

// ---------------------------------------------------------------------------
// Editor: single section render (live preview of unsaved changes)
// ---------------------------------------------------------------------------

export interface RenderSectionPayload {
  sectionId: string;
  sectionConfig: any;
  productId?: string;
  collectionId?: string;
  globalSettings?: Record<string, any>;
  groupName?: SectionGroup;
}

export async function renderSection(
  session: EditorSession,
  payload: RenderSectionPayload
): Promise<{ html: string; timing: number }> {
  const url = `${ENGINE_BASE}/render-section/${session.tenantId}/${session.themeId}`;
  return request(url, {
    method: 'POST',
    headers: headers(session),
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Editor: persistence
// ---------------------------------------------------------------------------

export async function saveSectionSettings(
  session: EditorSession,
  body: {
    group: SectionGroup;
    page?: string;
    sectionId: string;
    /**
     * Tipo da section — permite que o backend UPSERT caso a entry
     * ainda não exista no template.json (proteção contra race
     * entre POST /sections e PUT /section-settings no autosave).
     */
    type?: string;
    settings: Record<string, any>;
    blocks?: Record<string, any>;
    blockOrder?: string[];
    disabled?: boolean;
  }
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/section-settings`;
  await request(url, { method: 'PUT', headers: headers(session), body: JSON.stringify(body) });
}

export async function saveSectionOrder(
  session: EditorSession,
  body: { group: SectionGroup; page?: string; order: string[] }
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/section-order`;
  await request(url, { method: 'PUT', headers: headers(session), body: JSON.stringify(body) });
}

export async function addSection(
  session: EditorSession,
  body: {
    group: SectionGroup;
    page?: string;
    sectionId: string;
    section: Partial<SectionMetadata> & { type: string };
    index?: number;
  }
): Promise<{ sectionId: string }> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/sections`;
  return request(url, { method: 'POST', headers: headers(session), body: JSON.stringify(body) });
}

export async function removeSection(
  session: EditorSession,
  params: { group: SectionGroup; page?: string; sectionId: string }
): Promise<void> {
  const qs = new URLSearchParams({
    group: params.group,
    ...(params.page ? { page: params.page } : {}),
  }).toString();
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/sections/${params.sectionId}?${qs}`;
  await request(url, { method: 'DELETE', headers: headers(session) });
}

export async function saveGlobalSettings(
  session: EditorSession,
  settings: Record<string, any>
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/global-settings`;
  await request(url, { method: 'PUT', headers: headers(session), body: JSON.stringify({ settings }) });
}

// ---------------------------------------------------------------------------
// Code mode: raw file IO
// ---------------------------------------------------------------------------

export async function listThemeFiles(session: EditorSession): Promise<string[]> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/files`;
  const r = await request<{ files: string[] }>(url, { method: 'GET', headers: headers(session) });
  return r.files;
}

export async function readThemeFile(session: EditorSession, path: string): Promise<string> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/file?path=${encodeURIComponent(path)}`;
  const r = await request<{ content: string }>(url, { method: 'GET', headers: headers(session) });
  return r.content;
}

export async function writeThemeFile(
  session: EditorSession,
  path: string,
  content: string
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/file`;
  await request(url, {
    method: 'PUT',
    headers: headers(session),
    body: JSON.stringify({ path, content }),
  });
}

/**
 * Delete de arquivo do tema. Suporta a opção "Excluir" do context menu
 * do file tree (code mode).
 *
 * Backend valida `path` contra whitelisted prefixes; caller aqui não
 * precisa duplicar essa checagem, mas deve confirmar com o usuário
 * antes de chamar (delete é destrutivo — sem lixeira do lado do S3
 * pra esse fluxo).
 */
export async function deleteThemeFile(
  session: EditorSession,
  path: string
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/file?path=${encodeURIComponent(path)}`;
  await request(url, {
    method: 'DELETE',
    headers: headers(session),
  });
}

/**
 * Baixa todo o tema como ZIP (endpoint `GET /editor/.../download`).
 *
 * Fluxo: fetch com Bearer → body como blob → cria URL temporária →
 * clica anchor invisível pra disparar o download → revoga URL.
 *
 * Filename vem do header `Content-Disposition` do server; se ausente,
 * fallback pra `theme-{themeId}.zip`. Jogamos toast/error nas mãos do
 * caller (aqui só retornamos void em sucesso, throw em falha).
 *
 * Bug/feature (LC-1466): "baixar tema — adicionar opção de baixar o tema".
 */
/**
 * Refatorado abaixo pra `downloadThemeArchive` — mantido export por
 * compatibilidade (o botão do Toolbar já usa `downloadTheme`).
 */
export async function downloadTheme(session: EditorSession): Promise<void> {
  return downloadThemeArchive(session);
}

/**
 * Download flexível de tema como ZIP.
 *
 * - Sem `prefix` → baixa tudo (tema inteiro), como antes.
 * - Com `prefix` → só arquivos abaixo daquele path (ex.: "sections/").
 *   Frontend usa isso pro item "Baixar pasta (ZIP)" do context menu.
 *
 * Backend engine já aceita `?prefix=X` no `/editor/.../download`
 * (validado contra whitelisted prefixes).
 */
export async function downloadThemeArchive(
  session: EditorSession,
  prefix?: string,
): Promise<void> {
  const qs = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/download${qs}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.token}`,
      ...(session.storeHost ? { 'X-Store-Host': session.storeHost } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Falha ao baixar tema (HTTP ${res.status}): ${text.slice(0, 200)}`);
  }
  // Extrai filename do Content-Disposition (`attachment; filename="foo.zip"`).
  let filename = `theme-${session.themeId}.zip`;
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename\*?=['"]?(?:UTF-\d['']*)?([^;'"\s]+)['"]?/i);
  if (m && m[1]) filename = decodeURIComponent(m[1]);

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Espera o browser iniciar o download antes de revogar
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Download de um arquivo ÚNICO do tema (não zip). Usado pelo item
 * "Download" do context menu do file tree quando o alvo é um file.
 *
 * Usa `readThemeFile` (endpoint GET file) e cria um blob local com o
 * content-type inferido da extensão. Evita adicionar endpoint novo no
 * engine só pra isso — o file tree já sabe qual conteúdo é.
 */
export async function downloadThemeFile(
  session: EditorSession,
  path: string,
): Promise<void> {
  const content = await readThemeFile(session, path);
  const filename = path.split('/').pop() || 'file.txt';
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeByExt: Record<string, string> = {
    liquid: 'text/plain',
    html: 'text/html',
    json: 'application/json',
    css: 'text/css',
    scss: 'text/css',
    js: 'application/javascript',
    ts: 'application/typescript',
    svg: 'image/svg+xml',
    txt: 'text/plain',
    md: 'text/markdown',
    xml: 'application/xml',
    yml: 'text/yaml',
    yaml: 'text/yaml',
  };
  const mime = mimeByExt[ext] || 'application/octet-stream';
  const blob = new Blob([content], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

// ---------------------------------------------------------------------------
// CDN asset browser (lc-store-assets-storage)
// ---------------------------------------------------------------------------

export interface CdnAsset {
  name: string;
  url: string;
  source?: string;
  size?: number;
  mime?: string;
  /*
    Quando o upload é um MP4, o engine extrai o primeiro frame e sobe
    como `<name>.cover.jpg`. O editor pode usar essa URL pra auto-popular
    o setting `cover_image` do block — sem o seller precisar fazer
    upload manual da capa.
  */
  cover?: { name: string; url: string } | null;
}

export async function listCdnAssets(session: EditorSession): Promise<CdnAsset[]> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/cdn-assets`;
  const r = await request<{ assets: CdnAsset[] }>(url, {
    method: 'GET',
    headers: headers(session),
  });
  return r.assets;
}

// ---------------------------------------------------------------------------
// Theme sync (pull latest cdn/default/ into the tenant's theme)
// ---------------------------------------------------------------------------

export interface ThemeSyncReport {
  upgraded: boolean;
  from: number | null;
  to: number;
  files: { overwritten: number; created: number; skipped: number };
  settings: { added: string[]; unchanged: string[] };
  skipped?: string;
}

export async function syncThemeFromDefault(
  session: EditorSession,
  opts?: { force?: boolean }
): Promise<ThemeSyncReport> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/sync-from-default`;
  return request(url, {
    method: 'POST',
    headers: headers(session),
    body: JSON.stringify({ force: !!opts?.force }),
  });
}

// ---------------------------------------------------------------------------
// Luna Intelligence (AI assistant)
// ---------------------------------------------------------------------------

export interface LunaBalance {
  balance: number;
  unlimited: boolean;
  freeGrant: number;
  model: string;
  configured: boolean;
}

export async function fetchLunaBalance(session: EditorSession): Promise<LunaBalance> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/luna/balance`;
  return request(url, { method: 'GET', headers: headers(session) });
}

export async function fetchCustomizedFiles(
  session: EditorSession
): Promise<string[]> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/customized-files`;
  const r = await request<{ files: string[] }>(url, {
    method: 'GET',
    headers: headers(session),
  });
  return r.files || [];
}

export interface LunaChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface LunaContextPayload {
  page?: string;
  selectedSectionId?: string;
  openFile?: { path: string; selection?: string };
  globalSettings?: Record<string, any>;
}

export type LunaEvent =
  | { type: 'text'; delta: string }
  | { type: 'tool_use'; id: string; name: string; input: any }
  | { type: 'tool_result'; id: string; summary: string }
  | {
      type: 'proposal';
      proposal: {
        proposalId: string;
        kind: 'write' | 'delete';
        path: string;
        newContent?: string;
        oldContent?: string | null;
        reason?: string;
      };
    }
  | {
      type: 'done';
      stopReason: string;
      usage: { inputTokens: number; outputTokens: number };
      credits: {
        spent: number;
        balanceAfter: number;
        /**
         * True quando a cobrança pra paycentral falhou. Editor deve
         * mostrar warning visível — sem isso o user usa Luna de graça
         * silenciosamente.
         */
        chargeFailed?: boolean;
        chargeError?: string;
        /** False quando LUNA_DEV_UNLIMITED tá ativo (não cobra mesmo) */
        enforced?: boolean;
      };
    }
  | { type: 'error'; message: string };

/**
 * Streams a Luna chat turn. `onEvent` is invoked for every SSE frame as
 * it arrives. Returns a promise that resolves when the stream ends and
 * rejects if the network drops. Caller passes the full conversation up
 * to the latest user message — the engine is stateless between turns.
 */
export async function streamLunaChat(
  session: EditorSession,
  params: {
    messages: LunaChatMessage[];
    context: LunaContextPayload;
    conversationId?: string;
    signal?: AbortSignal;
  },
  onEvent: (ev: LunaEvent) => void
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/luna/chat`;
  const res = await fetch(url, {
    method: 'POST',
    headers: headers(session, { Accept: 'text/event-stream' }),
    body: JSON.stringify(params),
    signal: params.signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    let msg = `HTTP ${res.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) msg = parsed.error;
    } catch {}
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by blank lines. Each frame is one or
    // more `field: value` pairs. We only care about `data:` lines.
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const rawFrame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const dataLines = rawFrame
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart());
      if (dataLines.length === 0) continue;
      const payload = dataLines.join('\n');
      try {
        onEvent(JSON.parse(payload) as LunaEvent);
      } catch {
        // Ignore malformed frames — the server shouldn't send them,
        // but if it does, dropping one is better than bricking the
        // whole stream.
      }
    }
  }
}

export async function applyLunaProposal(
  session: EditorSession,
  payload: { path: string; content: string; kind?: 'write' | 'delete' }
): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/luna/apply`;
  await request(url, {
    method: 'POST',
    headers: headers(session),
    body: JSON.stringify({ kind: 'write', ...payload }),
  });
}

// ---------------------------------------------------------------------------
// Luna Intelligence — credit packages (proxied through engine → paycentral)
// ---------------------------------------------------------------------------

export interface LunaPackage {
  id: number;
  slug: string;
  name: string;
  credits: number;
  bonus_credits: number;
  price_cents: number;
  currency: string;
}

export async function listLunaPackages(session: EditorSession): Promise<LunaPackage[]> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/luna/packages`;
  const r = await request<{ packages: LunaPackage[] }>(url, {
    method: 'GET',
    headers: headers(session),
  });
  return r.packages || [];
}

export interface LunaCheckout {
  invoice_id: number;
  gateway_charge_id: string;
  payment_link?: string | null;
  amount_cents: number;
  credits: number;
  qr_code_base64?: string;
  pix_copy_paste?: string;
  pix_expires_at?: string;
  package: LunaPackage;
}

export async function purchaseLunaPackage(
  session: EditorSession,
  params: { packageSlug: string; billingType: 'PIX' | 'CREDIT_CARD' | 'BOLETO' }
): Promise<LunaCheckout> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/luna/purchase`;
  const r = await request<{ checkout: LunaCheckout }>(url, {
    method: 'POST',
    headers: headers(session),
    body: JSON.stringify({
      package_slug: params.packageSlug,
      billing_type: params.billingType,
    }),
  });
  return r.checkout;
}

// ---------------------------------------------------------------------------
// Store info — proxy pra paycentral GET /stores/{uuid}
// Usado pelo botão "Ver loja" no Toolbar pra decidir entre:
//   - Abrir https://{domain_store} (loja tem domínio configurado)
//   - Mostrar modal "configure seu domínio" (domain_store === null)
// ---------------------------------------------------------------------------

export interface StoreInfo {
  tenantId: string;
  /** Domínio público da vitrine (CNAME pra engine). null se não configurado. */
  domain_store: string | null;
  /** Domínio do checkout (sempre setado, fallback pra subdomínio). */
  domain_checkout: string | null;
  store_name: string | null;
}

export async function fetchStoreInfo(session: EditorSession): Promise<StoreInfo> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/store-info`;
  return request<StoreInfo>(url, { method: 'GET', headers: headers(session) });
}

// ---------------------------------------------------------------------------
// Pickers — produtos e coleções paginados com busca (consumidos pelos
// modais de ProductField/CollectionField).
//
// O metadata inicial só vem com os 20 primeiros itens — pra loja com 500+
// produtos, o lojista precisava de search/paginação. Esses endpoints
// proxificam o paycentral com page+q reais.
// ---------------------------------------------------------------------------

export interface PickerProduct {
  uuid: string;
  name: string;
  image: string;
  amount: number;
  sku: string;
  active: boolean;
}

export interface PickerCollection {
  uuid: string;
  handle: string;
  title: string;
  image: string;
  description: string;
}

export interface PaginatedPickerResult<T> {
  page: number;
  total: number;
  totalPages: number;
  perPage: number;
  /** Items — `products` ou `collections` conforme o endpoint. */
  products?: T[];
  collections?: T[];
}

export async function fetchPickerProducts(
  session: EditorSession,
  params?: { q?: string; page?: number }
): Promise<PaginatedPickerResult<PickerProduct>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.page) qs.set('page', String(params.page));
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/products${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  return request(url, { method: 'GET', headers: headers(session) });
}

export async function fetchPickerCollections(
  session: EditorSession,
  params?: { page?: number }
): Promise<PaginatedPickerResult<PickerCollection>> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/collections-list${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  return request(url, { method: 'GET', headers: headers(session) });
}

// ---------------------------------------------------------------------------
// Reviews — CRUD + gerador
// ---------------------------------------------------------------------------

export interface Review {
  id: string;
  product_uuid: string | null;
  origin_scope?: { kind: string; uuid?: string };
  author_name: string;
  author_gender?: 'M' | 'F';
  rating: 1 | 2 | 3 | 4 | 5;
  text: string;
  verified: boolean;
  status: 'approved' | 'pending' | 'rejected';
  created_at: number;
  updated_at?: number;
  generated: boolean;
}

export interface ReviewsListResult {
  reviews: Review[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface Niche {
  id: string;
  label: string;
  subniches?: Array<{ id: string; label: string }>;
}

export async function fetchReviewNiches(session: EditorSession): Promise<Niche[]> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/reviews/niches`;
  const r = await request<{ niches: Niche[] }>(url, { method: 'GET', headers: headers(session) });
  return r.niches || [];
}

export async function fetchReviews(
  session: EditorSession,
  params?: { product_uuid?: string; status?: string; page?: number; per_page?: number }
): Promise<ReviewsListResult> {
  const qs = new URLSearchParams();
  if (params?.product_uuid) qs.set('product_uuid', params.product_uuid);
  if (params?.status) qs.set('status', params.status);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.per_page) qs.set('per_page', String(params.per_page));
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/reviews${
    qs.toString() ? `?${qs.toString()}` : ''
  }`;
  return request(url, { method: 'GET', headers: headers(session) });
}

export async function updateReview(
  session: EditorSession,
  id: string,
  patch: Partial<Pick<Review, 'text' | 'author_name' | 'rating' | 'verified' | 'status'>>
): Promise<Review> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/reviews/${id}`;
  const r = await request<{ review: Review }>(url, {
    method: 'PUT',
    headers: headers(session),
    body: JSON.stringify(patch),
  });
  return r.review;
}

export async function deleteReview(session: EditorSession, id: string): Promise<void> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/reviews/${id}`;
  await request(url, { method: 'DELETE', headers: headers(session) });
}

export type ReviewDistribution =
  | 'mostly_5'
  | 'balanced_high'
  | 'realistic'
  | 'only_5'
  | { 5: number; 4: number; 3: number; 2: number; 1: number };

export interface GenerateReviewsParams {
  destination:
    | { kind: 'all' }
    | { kind: 'product'; uuid: string }
    | { kind: 'collection'; uuid: string };
  total: number;
  niche?: string;
  subniche?: string;
  gender: 'M' | 'F' | 'mixed';
  distribution: ReviewDistribution;
  verified: boolean;
  auto_approve: boolean;
}

export interface GenerateReviewsResult {
  status: string;
  generated: number;
  scope: any;
  reviewIds: string[];
  spreadAcross?: number;
}

export async function generateReviewsApi(
  session: EditorSession,
  params: GenerateReviewsParams
): Promise<GenerateReviewsResult> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/reviews/generate`;
  return request(url, {
    method: 'POST',
    headers: headers(session),
    body: JSON.stringify(params),
  });
}

export async function uploadCdnAsset(
  session: EditorSession,
  file: File
): Promise<CdnAsset> {
  const url = `${ENGINE_BASE}/editor/${session.tenantId}/${session.themeId}/cdn-assets`;
  const fd = new FormData();
  fd.append('file', file);
  // Don't pass `Content-Type` — the browser will add it with the correct
  // multipart boundary. We do need the auth header though.
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.token}`,
      ...(session.storeHost ? { 'X-Store-Host': session.storeHost } : {}),
    },
    body: fd,
  });
  const text = await res.text();
  let data: any;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = typeof data === 'object' && data?.error ? data.error : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data as CdnAsset;
}
